import { apiClient } from '@/api/client';
import { GUEST_STATUS_TIMEOUT_MS, REGISTRATION_TOKEN_HEADER } from '../constants/guest-registration';
import type { GuestRegistrationInput, GuestRegistrationResponse, GuestRegistrationStatus } from '../types';

/**
 * Public (account-less) event registration reads and writes (FR-282, FR-286).
 * Both routes are `@Public()` on the backend. `/v1` is supplied by
 * `API_BASE_URL`, so the paths are version-free.
 */

/**
 * Register a visitor without an account. The answer is identical for a new and
 * a repeat registration (NFR-32), so nothing here can tell them apart.
 *
 * Rejects (propagates) on a refusal: `EVENT_NOT_REGISTERABLE`,
 * `EVENT_REGISTRATION_CLOSED` or a 429. The body is never logged.
 *
 * @returns the registration token the page remembers.
 */
export async function registerGuest(eventId: string, input: GuestRegistrationInput): Promise<GuestRegistrationResponse> {
  const { data } = await apiClient.post<GuestRegistrationResponse>(
    `/events/${encodeURIComponent(eventId)}/guest-registrations`,
    input,
  );
  // A 201 with no token cannot be remembered or shown; fail it rather than store `undefined`.
  if (typeof data?.registrationToken !== 'string' || data.registrationToken.length === 0) {
    throw new Error('Unexpected guest registration response');
  }
  return { registrationToken: data.registrationToken };
}

/**
 * Whether a stored token still identifies a live registration for this event,
 * and its join details if so. The token travels in a header only, never the
 * URL, so it stays out of access logs. Always answers 200: `isRegistered:
 * false` means the token is stale and nothing else is disclosed. Times out after
 * `GUEST_STATUS_TIMEOUT_MS`, rejecting like any failed read.
 */
export async function fetchGuestRegistrationStatus(eventId: string, token: string): Promise<GuestRegistrationStatus> {
  const { data } = await apiClient.get<Partial<GuestRegistrationStatus> | null>(
    `/events/${encodeURIComponent(eventId)}/guest-registration`,
    { headers: { [REGISTRATION_TOKEN_HEADER]: token }, timeout: GUEST_STATUS_TIMEOUT_MS },
  );
  const isRegistered = data?.isRegistered === true;
  return {
    isRegistered,
    locationType: isRegistered ? (data?.locationType ?? null) : null,
    locationDetails: isRegistered && typeof data?.locationDetails === 'string' ? data.locationDetails : null,
  };
}

/**
 * Cancel a remembered non-member's registration (FR-289). The token travels in
 * the `X-Registration-Token` header only, never the URL, so it stays out of
 * access logs. The route answers an empty `204` for EVERY token, valid or not,
 * so it cannot be used to probe; that is why the page clears its own entry on
 * success rather than waiting to be told anything.
 *
 * Rejects (propagates) on a failure such as a 429. The token is never logged.
 */
export async function unregisterGuest(eventId: string, token: string): Promise<void> {
  await apiClient.delete(`/events/${encodeURIComponent(eventId)}/guest-registration`, {
    headers: { [REGISTRATION_TOKEN_HEADER]: token },
  });
}
